import { useLocation, useNavigate } from 'react-router-dom';

import classNames from 'classnames';

import { capitalize, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import CustomIcon from 'assets/Icon';
import Flex from 'modules/shared/components/general/Flex/Flex';

import { BrandStatusLabel, IBrand } from 'types/brands.types';

interface IBrandCardProps {
    brand: IBrand;
}

export default function BrandCard({ brand }: IBrandCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  
  const onBrandClick = () => {
    navigate({ pathname: `${location.pathname}/${brand.id}` });
  };

  const disabled =
      brand.email_status === BrandStatusLabel.REGENERATING || brand.email_status === BrandStatusLabel.SENDING || brand.email_status === BrandStatusLabel.SENT;

  const enabledWithStatus =
      brand.email_status === BrandStatusLabel.REGENERATED || brand.email_status === BrandStatusLabel.FAILED_TO_SEND || brand.email_status === BrandStatusLabel.FAILED_TO_REGENERATE;

  const sent = brand.email_status === BrandStatusLabel.SENT;

  const getTooltipTitle = () => {
    if (brand.email_status === BrandStatusLabel.REGENERATING) {
      return 'Regenerating...';
    }
    if (brand.email_status === BrandStatusLabel.SENDING) {
      return 'Sending...';
    }
    if (brand.email_status === BrandStatusLabel.REGENERATED) {
      return 'Regenerated.';
    }
    if (brand.email_status === BrandStatusLabel.FAILED_TO_REGENERATE) {
      return 'Failed to regenerate. Please try again.';
    }
    if (brand.email_status === BrandStatusLabel.FAILED_TO_SEND) {
      return 'Failed to send. Please try again.';
    }

    return '';
  };

  return (
    <div
      className={classNames('brand-wrapper')}
    >
      {((disabled && brand.email_status !== BrandStatusLabel.SENT) || enabledWithStatus) && (
        <Tooltip
          title={getTooltipTitle()}
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#444444',
                fontSize: 18,
                fontWeight: 'normal',
              },
            },
            arrow: {
              sx: {
                color: '#444444',
              },
            },
          }}
          PopperProps={{
            sx: {
              backgroundColor: 'transparent',
            },
          }}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -4],
                  },
                },
              ],
            },
          }}>
          {brand.email_status === BrandStatusLabel.FAILED_TO_REGENERATE || brand.email_status === BrandStatusLabel.FAILED_TO_SEND ? (
            <CustomIcon name="warning_outlined" fontSize="small" sx={{ position: 'absolute', top: 15, right: 15, zIndex: 1000, color: "#B3261E" }} />
          ) : (
            <InfoOutlinedIcon fontSize="small" sx={{ position: 'absolute', top: 15, right: 15, zIndex: 1000, color: "#636363" }} />
          )}
        </Tooltip>
      )}
      {sent && (
        <Flex alignItems="center" gap="6px" style={{ position: 'absolute', top: 15, right: 15, zIndex: 1000 }}>
          <span className="sent-status-date">
            <strong>{capitalize(brand.email_status)}</strong> {brand.updated_at}
          </span>
          <CheckCircleIcon style={{ fontSize: 18, color: "#33850D" }} />
        </Flex>
      )}
      <div
        className={classNames('brands', {
          disabled,
        })}
        onClick={disabled ? undefined : onBrandClick}
      >
        <img src={brand.logo_url} alt="Brand Logo"
          style={{ maxHeight: '120px', maxWidth: '120px', objectFit: 'cover' }}
        />
      </div>
    </div>
  );
}
